import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { themeStylesheet, type ThemeMode } from "@csbi/viz-core"

export type ThemePreference = ThemeMode | "system"

type ThemeContextValue = {
  preference: ThemePreference
  mode: ThemeMode
  setPreference: (next: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)
const STORAGE_KEY = "csbi.theme"
const STYLE_ID = "csbi-tokens"

function readPreference(): ThemePreference {
  const raw = globalThis.localStorage?.getItem(STORAGE_KEY)
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system"
}

function systemMode(): ThemeMode {
  return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readPreference)
  const [system, setSystem] = useState<ThemeMode>(systemMode)

  useEffect(() => {
    const mq = globalThis.matchMedia?.("(prefers-color-scheme: dark)")
    if (!mq) return
    const onChange = () => setSystem(mq.matches ? "dark" : "light")
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [])

  const mode: ThemeMode = preference === "system" ? system : preference

  useEffect(() => {
    let style = document.getElementById(STYLE_ID)
    if (!style) {
      style = document.createElement("style")
      style.id = STYLE_ID
      document.head.append(style)
    }
    style.textContent = themeStylesheet()
  }, [])

  useEffect(() => {
    document.documentElement.dataset.theme = mode
    document.documentElement.style.colorScheme = mode
  }, [mode])

  const setPreference = useCallback((next: ThemePreference) => {
    setPref(next)
    globalThis.localStorage?.setItem(STORAGE_KEY, next)
  }, [])

  const value = useMemo(() => ({ preference, mode, setPreference }), [preference, mode, setPreference])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error("useTheme must be called inside ThemeProvider")
  return ctx
}
